import {ShopContext, UserModel} from '../auth/core/_models'

/**
 * A supplier that runs a partner SHOP (store) manages its shelf, not national supplier stock: the
 * server sends its quantities as `shop_quantity` with `stock_scope: "shop"`, and every stock write
 * from this account lands on the store's shelf. These helpers keep that one rule in one place.
 */

/** Which stock a row's quantity is: the store's shelf, or the supplier's national stock. */
export type StockScope = 'shop' | 'national'

/** Is this row's quantity a store shelf? (The server sets `stock_scope` per row.) */
export const isShopScoped = (row: {stock_scope?: string | null}): boolean => row.stock_scope === 'shop'

/** The store this signed-in supplier runs, or null. */
export const shopOf = (user?: UserModel | null): ShopContext | null => user?.shop ?? null

export const isShopUser = (user?: UserModel | null): boolean => shopOf(user) !== null

/** "Kilos (KILOS)" — the store as the banner and labels name it. */
export const shopLabel = (shop: ShopContext): string =>
  shop.code && shop.code !== shop.name ? `${shop.name} (${shop.code})` : shop.name

/**
 * The quantity a row should show and edit: the shelf when the row is shop-scoped, else the
 * supplier's national quantity.
 */
export const displayQuantity = (row: {
  stock_scope?: string
  shop_quantity?: number | null
  supplier_quantity?: number | string | null
  quantity?: number | null
}): number => {
  if (isShopScoped(row)) {
    return Number(row.shop_quantity ?? 0)
  }
  return Number(row.supplier_quantity ?? row.quantity ?? 0)
}

/**
 * Shelf refusals the server answers with 422 / 503 (ShopShelfRefused, shop_binding_unreadable).
 * `expiry_required` and `bad_expiry` are no longer sent for a shop (dumyah#2101); they stay for one
 * release so a server without that change still has its refusal explained. Remove them after.
 */
export const SHELF_ERRORS = [
  'expiry_required',
  'below_reserved',
  'package_not_for_shop',
  'bad_expiry',
  'bad_quantity',
  'no_offer',
  'no_bin',
  'bin_placement_failed',
  'not_a_shop',
  'shop_unavailable',
  'shop_binding_unreadable',
  'shop_branch_required',
] as const

export type ShelfError = typeof SHELF_ERRORS[number]

/** The shelf refusal in an axios error, or null when it is something else. */
export const shelfErrorOf = (err: any): {error: ShelfError; message: string; held: number} | null => {
  const body = err?.response?.data
  const code = body?.error
  if (typeof code !== 'string' || !(SHELF_ERRORS as readonly string[]).includes(code)) {
    return null
  }
  return {error: code as ShelfError, message: String(body?.message ?? ''), held: Number(body?.held ?? 0)}
}

/** Minutes since the store last updated its stock, or null when it never has. */
export const minutesSinceStockUpdate = (shop: ShopContext, now: Date = new Date()): number | null => {
  if (!shop.last_stock_update_at) {
    return null
  }
  const at = new Date(shop.last_stock_update_at).getTime()
  if (!Number.isFinite(at)) {
    return null
  }
  return Math.max(0, Math.floor((now.getTime() - at) / 60000))
}

/** True when the store is past its "update your stock" period (or has never updated). */
export const stockUpdateOverdue = (shop: ShopContext, now: Date = new Date()): boolean => {
  const minutes = minutesSinceStockUpdate(shop, now)
  return minutes === null || minutes >= (shop.alert_after_minutes ?? 60)
}
